import { Session } from 'next-auth'
import { getSession } from 'next-auth/react'
import { assertApiErrorOrThrow } from '../errors/apiError'
import { ExpApiErrorCode } from '../errors/models/errorTypes'

export enum HTTPMethodType {
	GET = 'GET',
	POST = 'POST',
	PUT = 'PUT',
	DELETE = 'DELETE',
	PATCH = 'PATCH',
}

export enum HTTPDestinationType {
	INTERNAL = 'INTERNAL',
	EXTERNAL = 'EXTERNAL',
}

type APICallOptionsType = {
	query?: object
	body?: object
	token?: string
	headers?: Record<string, string>
	signal?: AbortSignal
}

/**
 * Retrieves the access token from the current session.
 *
 * @returns A promise that resolves to the access token as a string, or undefined if no token is found.
 */
const getAuthToken = async () => {
	const session: Session | null = await getSession({ broadcast: false })
	const token = session?.accessToken ?? null
	return token
}

/**
 * Builds a set of HTTP headers for API requests.
 *
 * @param {string} authorization - The authorization token to be included in the headers.
 * @returns {Record<string, string>} An object containing the HTTP headers, including:
 * - `Content-Type`: Specifies the content type as `application/json`.
 * - `x-hous-version`: Specifies the API version as `v1.0`.
 * - `authorization`: Includes the provided authorization token.
 *
 */
export function buildHeaders(authorization: string): Record<string, string> {
	return {
		'Content-Type': 'application/json',
		'x-hous-version': 'v1.0',
		'Authorization': authorization,
	}
}

/**
 * Retrieves the authorization header for an HTTP request based on the destination type.
 *
 * @param {HTTPDestinationType} type - The destination type of the HTTP request (e.g., INTERNAL or EXTERNAL).
 * @returns {Promise<string>} A promise that resolves to the authorization header string.
 * - If the destination type is `HTTPDestinationType.EXTERNAL`, it returns a `Bearer` token.
 * - If the destination type is `HTTPDestinationType.INTERNAL`, it returns an empty string.
 *
 */
async function getAuthorizationHeader(
	type: HTTPDestinationType
): Promise<string> {
	if (type === HTTPDestinationType.EXTERNAL) {
		const token = await getAuthToken()
		if (token !== null && token !== undefined) {
			return `Bearer ${token}`
		}
		assertApiErrorOrThrow(
			'getAuthorizationHeader failed! No token found. User not authorized.',
			ExpApiErrorCode.HTTP_ERROR
		)
	}
	return ''
}

/**
 * Constructs a full URL by appending query parameters to the base URL.
 *
 * @param {string} url - The base URL to which query parameters will be appended.
 * @param {object} query - An object containing key-value pairs of query parameters.
 * @returns {string} The full URL with query parameters appended. If the query object is empty, the base URL is returned as-is.
 *
 */
function constructFullUrl(url: string, query: object): string {
	const queryString = new URLSearchParams(
		query as Record<string, string>
	).toString()
	return queryString ? `${url}?${queryString}` : url
}

/**
 * Builds the request configuration for the fetch API.
 *
 * @param {string} method - The HTTP method to use (GET, POST, PUT, DELETE, etc.).
 * @param {object} body - The body of the request. Will be stringified for non-GET requests.
 * @param {string} authorization - The authorization header value.
 * @param {boolean} toCancelCache - Whether to cancel cache for the request.
 * @returns {RequestInit} The configuration object for the fetch API.
 */
function buildRequestConfig(
	method: string,
	body: object,
	authorization: string,
	toCancelCache: boolean,
	headers?: Record<string, string>
): RequestInit {
	let finalHeaders: Record<string, string> = buildHeaders(authorization)
	if (headers) {
		finalHeaders = { ...finalHeaders, ...headers }
	}
	return {
		method,
		headers: finalHeaders,
		body: method !== 'GET' ? JSON.stringify(body) : undefined,
		signal: AbortSignal.timeout(60000),
		...(toCancelCache && { cache: 'no-store' }),
	}
}

/**
 * Perform a typed HTTP API call, returning the parsed `data` payload from a JSON response.
 *
 * @param type - The HTTP destination type (HTTPDestinationType) used to determine authorization / destination behavior.
 * @param method - The HTTP method to use (e.g. "GET", "POST", "PUT", "DELETE").
 * @param url - The request URL or path fragment to be combined with any base/host by `constructFullUrl`.
 * @param options - Optional request settings.
 * @param options.query - Query parameters to include in the request URL (defaults to {}).
 * @param options.body - Request body to include in the fetch config (defaults to {}).
 * @param options.token - Optional bearer token to use for authorization; if omitted, an authorization header will be obtained asynchronously.
 * @param toCancelCache - When true, instructs the request config to include cache-canceling behavior (default: false).
 *
 * @returns A Promise that resolves to the extracted response payload (the `data` property from the JSON response), typed as `unknown`.
 *
 * @throws Will throw an application/Exp API error (via `assertApiErrorOrThrow`) for non-2xx responses, JSON parse errors, or other request failures.
 *
 */
export async function apiCall(
	type: HTTPDestinationType,
	method: string,
	url: string,
	{
		query = {},
		body = {},
		token = undefined,
		headers = undefined,
		signal = undefined,
	}: APICallOptionsType = {},
	toCancelCache: boolean = false
): Promise<unknown> {
	let response: unknown

	const authorization =
		token != null ? `Bearer ${token}` : await getAuthorizationHeader(type)
	const fullUrl = constructFullUrl(url, query)
	const config = buildRequestConfig(
		method,
		body,
		authorization,
		toCancelCache,
		headers
	)
	if (signal) {
		config.signal = signal
	}

	try {
		const apiResponse = await fetch(fullUrl, config)
		const apiData = await apiResponse.json()

		// Return error for non-2xx responses
		if (apiResponse.ok) {
			response = apiData?.data
		} else {
			console.warn(`[httpAdapter] Request failed - Status: ${apiResponse.status}, URL: ${fullUrl}, Headers sent:`, config.headers)
			throw assertApiErrorOrThrow(
				apiData,
				`ExpAPI call failed: ${apiResponse.statusText}`,
				fullUrl
			)
		}
	} catch (error: unknown) {
		assertApiErrorOrThrow(error, ExpApiErrorCode.API_ERROR, fullUrl)
	}
	return response
}

/**
 * Performs an HTTP GET request to the specified URL.
 *
 * @param {HTTPDestinationType} type - The type of the API destination (e.g., INTERNAL or EXTERNAL).
 * @param {string} url - The endpoint URL for the GET request.
 * @param {object} options - Optional query parameters to append to the URL.
 * @param {boolean} toCancelCache - Indicates whether the request is for static content from S3.
 *
 */
export async function httpGet(
	type: HTTPDestinationType,
	url: string,
	options: APICallOptionsType = {},
	toCancelCache: boolean = false
) {
	return apiCall(type, HTTPMethodType.GET, url, options, toCancelCache)
}

/**
 * Performs an HTTP POST request to the specified URL via the API call function.
 *
 * @param type - The destination type for the HTTP request (e.g., INTERNAL or EXTERNAL).
 * @param url - The URL to send the POST request to.
 * @param options - Optional configuration options for the API call.
 * @param options.query - Query parameters to be appended to the URL (optional).
 * @param options.body - The body of the POST request (optional).
 *
 * @returns A promise that resolves with the result of the API call.
 *
 */
export async function httpPost(
	type: HTTPDestinationType,
	url: string,
	options: APICallOptionsType = {}
) {
	return apiCall(type, HTTPMethodType.POST, url, options, false)
}

/**
 * Performs an HTTP PUT request to the specified URL via the API call function.
 *
 * @param type - The destination type for the HTTP request
 * @param url - The URL to send the PUT request to
 * @param options - Optional configuration options for the API call
 * @returns A promise that resolves with the result of the API call
 *
 */
export async function httpPut(
	type: HTTPDestinationType,
	url: string,
	options: APICallOptionsType = {}
) {
	return apiCall(type, HTTPMethodType.PUT, url, options)
}

export async function httpPatch(
	type: HTTPDestinationType,
	url: string,
	options: APICallOptionsType = {}
) {
	return apiCall(type, HTTPMethodType.PATCH, url, options)
}

export async function httpDelete(
	type: HTTPDestinationType,
	url: string,
	options: APICallOptionsType = {}
) {
	return apiCall(type, HTTPMethodType.DELETE, url, options)
}
/**
 * Perform a typed HTTP API call, returning the response as a Blob (for file downloads).
 *
 * @param type - The HTTP destination type (HTTPDestinationType) used to determine authorization / destination behavior.
 * @param method - The HTTP method to use (e.g. "GET", "POST", "PUT", "DELETE").
 * @param url - The request URL or path fragment to be combined with any base/host by `constructFullUrl`.
 * @param options - Optional request settings.
 * @param options.query - Query parameters to include in the request URL (defaults to {}).
 * @param options.body - Request body to include in the fetch config (defaults to {}).
 * @param options.token - Optional bearer token to use for authorization; if omitted, an authorization header will be obtained asynchronously.
 * @param toCancelCache - When true, instructs the request config to include cache-canceling behavior (default: false).
 *
 * @returns A Promise that resolves to the Blob response.
 *
 * @throws Will throw an application/Exp API error (via `assertApiErrorOrThrow`) for non-2xx responses or other request failures.
 */
export async function apiCallBlob(
	type: HTTPDestinationType,
	method: string,
	url: string,
	{
		query = {},
		body = {},
		token = undefined,
		headers = undefined,
	}: APICallOptionsType = {},
	toCancelCache: boolean = false
): Promise<unknown> {
	let response: unknown
	const authorization =
		token != null ? `Bearer ${token}` : await getAuthorizationHeader(type)
	const fullUrl = constructFullUrl(url, query)
	const config = buildRequestConfig(
		method,
		body,
		authorization,
		toCancelCache,
		headers
	)
	try {
		const apiResponse = await fetch(fullUrl, config)
		const apiData = await apiResponse.blob()

		// Return error for non-2xx responses
		if (apiResponse.ok) {
			response = apiData
		} else {
			throw assertApiErrorOrThrow(
				apiData,
				`ExpAPI call failed: ${apiResponse.statusText}`
			)
		}
	} catch (error: unknown) {
		assertApiErrorOrThrow(error, ExpApiErrorCode.API_ERROR)
	}
	return response
}
